import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  startWith,
  takeUntil,
} from 'rxjs';
import {
  DebtorCourtProcedureDialogComponent,
  DebtorCourtProcedureDialogData,
} from 'src/app/dialogs/case/debtor-court-procedure-dialog/debtor-court-procedure-dialog.component';
import { getCompanyBlockDialogData, isTerminatedSoleEntrepreneur } from 'src/app/dialogs/case/debtor-court-procedure-dialog/company-block-dialog.util';
import { EnumService } from 'src/app/services/enum/enum.service';
import { DebtorFormValue } from 'src/app/services/form/form.controls';
import { DEBTOR_FORM_NAME } from 'src/app/services/form/form.factory';
import { FormService } from 'src/app/services/form/form.service';
import {
  CompanySearchAutocompleteItem,
  CompanySearchService,
} from 'src/app/shared/company-search-service/company-search.service';
import { PayeeIconComponent } from '../../../../shared/payee-icon/payee-icon.component';
import { AutoFillSectionComponent } from '../invoice/auto-fill-section/auto-fill-section.component';
import { PayeeDropdownComponent } from '../../../../shared/payee-form-fields/payee-dropdown/payee-dropdown.component';
import { MatIcon } from '@angular/material/icon';
import {
  MatAutocompleteOrigin,
  MatAutocompleteTrigger,
  MatAutocomplete,
  MatOption,
} from '@angular/material/autocomplete';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatError } from '@angular/material/input';
import { PayeeFormFieldComponent } from '../../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeButtonComponent } from '../../../../shared/payee-buttons/payee-button/payee-button.component';
import { RouterLink } from '@angular/router';
import { CountryFlagPipe } from '../../../../shared/pipes/country-flag/country-flag.pipe';

@Component({
  selector: 'app-debtor-form',
  templateUrl: './debtor-form.component.html',
  styleUrls: ['./debtor-form.component.scss'],
  imports: [
    PayeeIconComponent,
    AutoFillSectionComponent,
    PayeeDropdownComponent,
    FormsModule,
    ReactiveFormsModule,
    MatIcon,
    MatAutocompleteOrigin,
    MatAutocompleteTrigger,
    MatProgressSpinner,
    MatAutocomplete,
    MatOption,
    MatError,
    PayeeFormFieldComponent,
    PayeeButtonComponent,
    RouterLink,
    CountryFlagPipe,
    TranslateModule,
  ],
})
export class DebtorFormComponent implements OnInit, OnDestroy {
  private formService = inject(FormService);
  private enumService = inject(EnumService);
  private translate = inject(TranslateService);
  private companySearchService = inject(CompanySearchService);
  private dialog = inject(MatDialog);

  readonly collapsed = this.formService.debtorCollapsed;
  readonly companyUpdateLoading = signal(false);

  private readonly destroy = new Subject<void>();
  /** Typing stream for async company search (not FormControl valueChanges). */
  private readonly companySearch$ = new Subject<string>();

  jurisdictionOptions: string[] = [];
  companySearchOptions: CompanySearchAutocompleteItem[] = [];

  addressSummary?: string;
  companyBlockDialogData: DebtorCourtProcedureDialogData | null = null;
  private companyBlockStatusTaxRoot: string | null = null;

  get showSaveRequiredError(): boolean {
    return this.formService.debtorSaveRequiredError && !this.collapsed();
  }

  customNameControl = new FormControl();

  get debtor() {
    return this.debtorForm?.getRawValue();
  }
  get debtorForm() {
    return this.formService.form.controls[DEBTOR_FORM_NAME];
  }
  get jurisdiction() {
    return this.debtorForm!.controls.jurisdiction;
  }
  get name() {
    return this.debtorForm!.controls.name;
  }
  get tax_number() {
    return this.debtorForm!.controls.tax_number;
  }
  get postcode() {
    return this.debtorForm!.controls.address.controls.postcode;
  }
  get settlement() {
    return this.debtorForm!.controls.address.controls.settlement;
  }
  get street() {
    return this.debtorForm!.controls.address.controls.street;
  }

  get companySearchEnabled(): boolean {
    return this.debtor?.jurisdiction === 'HU';
  }
  get autocompleteSearchLoading(): boolean {
    return this.companySearchService.autocompleteSearchLoading;
  }
  get isGuest(): boolean {
    return this.formService.isGuest;
  }

  async ngOnInit(): Promise<void> {
    if (!this.debtorForm) {
      return;
    }

    this.formService.debtorCollapsed.set(!this.debtorForm.invalid);

    await this.refreshDebtorFromCompanySearch();

    this.formService.formChange
      .pipe(takeUntil(this.destroy))
      .subscribe(() => {
        void this.lookupCompanyByTaxNumberIfNeeded();
      });

    this.debtorForm.valueChanges
      .pipe(takeUntil(this.destroy), startWith(this.debtor))
      .subscribe((v) => {
        this.customNameControl.patchValue(v?.name, { emitEvent: false });

        const address = v?.address;
        this.addressSummary = [
          address?.postcode ?? '',
          address?.settlement ?? '',
          address?.street ?? '',
        ]
          .join(' ')
          .trim();
      });

    this.companySearch$
      .pipe(
        takeUntil(this.destroy),
        debounceTime(500),
        distinctUntilChanged(),
        filter((name) => !!name && !this.collapsed() && this.companySearchEnabled),
      )
      .subscribe({
        next: async (name: string) => {
          try {
            this.companySearchOptions =
              await this.companySearchService.getCompanySearchAutocompleteItems(
                name,
              );
          } catch (error) {
            console.error('Error while company autocomplete', error);
          }
        },
      });

    this.tax_number?.valueChanges
      .pipe(takeUntil(this.destroy))
      .subscribe(() => {
        this.clearCompanyBlockStatus();
      });

    await this.setJurisdictionOptions();
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
    this.companySearch$.complete();
  }

  onCompanySearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.companySearch$.next(value);
  }

  switchDebtorType(debtor_type: DebtorFormValue['debtor_type']): void {
    if (this.debtor?.debtor_type === debtor_type) {
      return;
    }

    this.clearCompanyBlockStatus();
    this.debtorForm?.patchValue({
      debtor_type,
      tax_number: null,
      address: {
        country_iso: undefined,
        postcode: null,
        settlement: null,
        street: null,
      },
    });
    this.customNameControl.setValue(this.debtorForm?.value.name, {
      emitEvent: false,
    });
    this.companySearchOptions = [];
  }

  expandForm(): void {
    if (this.debtorForm?.disabled) {
      return;
    }

    this.formService.debtorSaveRequiredError = false;
    this.formService.debtorCollapsed.set(false);
  }

  async collapseIfValid(skipTaxLookup = false): Promise<void> {
    if (!this.debtorForm || this.companyUpdateLoading()) {
      return;
    }

    this.debtorForm.updateValueAndValidity();
    if (this.debtorForm.invalid) {
      this.expandForm();
      return;
    }

    this.formService.debtorSaveRequiredError = false;

    if (!skipTaxLookup) {
      await this.lookupCompanyByTaxNumberIfNeeded();
      this.debtorForm.updateValueAndValidity();
      if (this.debtorForm.invalid) {
        this.expandForm();
        return;
      }
    }

    const debtor = this.debtorForm.getRawValue();
    if (
      debtor.jurisdiction === 'HU'
      && debtor.tax_number
      && this.companyBlockStatusTaxRoot !== this.toTaxNumberRoot(debtor.tax_number)
    ) {
      try {
        const company = await this.companySearchService.exactSearch(debtor.tax_number);
        if (company) {
          await this.applyCompany(company);
          return;
        }
      } catch (error) {
        console.error('Error while resolving debtor company status', error);
        this.clearCompanyBlockStatus();
      }
    }

    if (await this.showBlockDialogIfNeeded()) {
      return;
    }

    this.syncCollapsedState();
  }

  async companySelected(company: CompanySearchAutocompleteItem): Promise<void> {
    await this.applyCompany(company);
    if (!this.companyBlockDialogData) {
      this.syncCollapsedState();
    }
  }

  private async applyCompany(company: CompanySearchAutocompleteItem): Promise<void> {
    if (!this.debtorForm) {
      return;
    }

    const jurisdiction = this.debtorForm.controls.jurisdiction.value ?? 'HU';
    const isEditable = this.debtorForm.controls.is_editable.value ?? true;
    const displayName = company.short_name || company.name;
    const isTerminatedInd = isTerminatedSoleEntrepreneur(company);
    const currentAddress = this.debtorForm.controls.address.getRawValue();

    this.debtorForm.patchValue({
      debtor_type: isTerminatedInd ? 'ind' : 'org',
      name: displayName,
      tax_number: isTerminatedInd ? null : company.tax_number,
      jurisdiction,
      is_editable: isEditable,
      party_type_id: isTerminatedInd ? null : company.party_type?.internal_id ?? null,
      address: {
        country_iso: company.country?.iso ?? currentAddress.country_iso ?? undefined,
        postcode: company.postcode || currentAddress.postcode,
        settlement: company.settlement || currentAddress.settlement,
        street: company.street || currentAddress.street,
      },
    });
    this.customNameControl.patchValue(displayName, { emitEvent: false });

    if (company.tax_number) {
      this.companyBlockStatusTaxRoot = this.toTaxNumberRoot(company.tax_number);
      this.companyBlockDialogData = isTerminatedInd
        ? { variant: 'terminated_sole_entrepreneur' }
        : company.party_type?.type === 'ind'
          ? null
          : getCompanyBlockDialogData(company.status, company.court_procedure_type);
    } else {
      this.clearCompanyBlockStatus();
    }

    this.debtorForm.updateValueAndValidity();

    if (await this.showBlockDialogIfNeeded()) {
      return;
    }

    if (this.debtorForm.invalid) {
      this.expandForm();
    }
  }

  private async showBlockDialogIfNeeded(): Promise<boolean> {
    if (!this.companyBlockDialogData) {
      return false;
    }

    const dialogData = this.companyBlockDialogData;
    await this.openCompanyBlockDialog(dialogData);
    this.formService.debtorCollapsed.set(false);

    if (dialogData.variant === 'terminated_sole_entrepreneur') {
      this.companyBlockDialogData = null;
    }

    return true;
  }

  private syncCollapsedState(): void {
    if (!this.debtorForm) {
      return;
    }

    if (this.debtorForm.invalid) {
      this.expandForm();
      return;
    }

    setTimeout(() => this.formService.debtorCollapsed.set(true), 100);
  }

  companyDisplayWith(company: CompanySearchAutocompleteItem | string): string {
    if (typeof company === 'string') return company;
    return company?.short_name || company?.name || '';
  }

  formatTaxNumber(taxNumber: string): string {
    if (!taxNumber) return '';
    const digits = taxNumber.replace(/\D/g, '');
    if (digits.length === 11) {
      return `${digits.slice(0, 8)}-${digits.slice(8, 9)}-${digits.slice(9, 11)}`;
    }
    return taxNumber;
  }

  formatCompanyAddress(option: CompanySearchAutocompleteItem): string {
    return [option.postcode, option.settlement, option.street].join(' ').trim();
  }

  readonly countryDisplayWith = (iso: string): string => {
    return this.translate.instant(`COUNTRY_NAMES.${iso}`, {
      default: new Intl.DisplayNames(this.translate.currentLang, {
        type: 'region',
      }).of(iso),
    });
  };

  private async refreshDebtorFromCompanySearch(): Promise<void> {
    await this.lookupCompanyByTaxNumberIfNeeded();
    await this.syncDebtorStateAfterCompanySearch();
  }

  private async syncDebtorStateAfterCompanySearch(): Promise<void> {
    if (!this.debtorForm) {
      return;
    }

    this.debtorForm.updateValueAndValidity();

    if (await this.showBlockDialogIfNeeded()) {
      return;
    }

    this.formService.debtorCollapsed.set(!this.debtorForm.invalid);
  }

  private async lookupCompanyByTaxNumberIfNeeded(): Promise<void> {
    if (!this.debtorForm) {
      return;
    }

    const taxNumber = this.debtorForm.getRawValue().tax_number;
    if (!this.shouldLookupCompanyByTaxNumber(taxNumber)) {
      return;
    }

    if (this.companyUpdateLoading()) {
      return;
    }

    this.companyUpdateLoading.set(true);
    try {
      const company = await this.companySearchService.exactSearch(taxNumber);
      if (!company) {
        return;
      }

      await this.applyCompany(company);
    } catch (error) {
      console.error('Error while exact company lookup by tax number', error);
    } finally {
      this.companyUpdateLoading.set(false);
    }
  }

  private clearCompanyBlockStatus(): void {
    this.companyBlockDialogData = null;
    this.companyBlockStatusTaxRoot = null;
  }

  private toTaxNumberRoot(taxNumber: string | null | undefined): string {
    return (taxNumber ?? '').replace(/\D/g, '').slice(0, 8);
  }

  private shouldLookupCompanyByTaxNumber(
    taxNumber: string | null | undefined,
  ): boolean {
    const debtor = this.debtorForm?.getRawValue();
    if (!taxNumber || debtor?.jurisdiction !== 'HU') {
      return false;
    }

    return this.toTaxNumberRoot(taxNumber).length >= 8;
  }

  private async openCompanyBlockDialog(
    dialogData: DebtorCourtProcedureDialogData,
  ): Promise<void> {
    const dialogRef = this.dialog.open(DebtorCourtProcedureDialogComponent, {
      data: dialogData,
      autoFocus: false,
    });

    await firstValueFrom(dialogRef.afterClosed());
  }

  private async setJurisdictionOptions(): Promise<void> {
    try {
      const countries = await this.enumService.getCountries();
      this.jurisdictionOptions = countries.map((c) => c.iso);
    } catch (error) {
      console.error('Error while loading countries', error);
    }
  }
}
